/**
 * Workspace-scan data model + pure helpers.
 *
 * A scan buckets a Slack workspace's content by CHANNEL, by CONTENT TYPE
 * (text / pics / video / files), and by TIME (all-time / last 1y / last 6mo /
 * last 90d) so the estimator can price any subset. Time buckets are NESTED: a
 * timestamp inside the last-90-days window also counts toward 6mo, 1y and
 * all-time. That makes selecting a time range a single bucket lookup.
 *
 * These helpers are pure (no Convex / no network) so they unit-test cheaply and
 * are shared by the scan action and the quote engine.
 */

import { v } from "convex/values";

export const TIME_BUCKETS = ["allTime", "last1y", "last6mo", "last90d"] as const;
export type TimeBucket = (typeof TIME_BUCKETS)[number];

/** A count + a byte total. Messages carry `bytes: 0` (we price them by count). */
export interface Metric {
  count: number;
  bytes: number;
}

/** The same metric measured across each (nested) time window. */
export type Bucketed = Record<TimeBucket, Metric>;

export type ContentType = "text" | "pics" | "video" | "files";

// Discord upload caps (bytes) by server boost level. Free & Level 1 = 10 MB;
// Level 2 = 50 MB; Level 3 = 100 MB. These govern migration (webhook) uploads —
// personal Nitro does NOT. Verified 2026.
export const UPLOAD_MB = 1024 * 1024;
export const UPLOAD_THRESHOLDS = {
  t10: 10 * UPLOAD_MB,
  t50: 50 * UPLOAD_MB,
  t100: 100 * UPLOAD_MB,
} as const;

/** Cumulative counts/bytes of media files ABOVE each Discord upload cap. */
export interface OverLimit {
  /** files larger than 10 MB (skipped at boost level 0/1). */
  over10: Metric;
  /** files larger than 50 MB (skipped at level 2). */
  over50: Metric;
  /** files larger than 100 MB (skipped at level 3 — the max). */
  over100: Metric;
}

export interface ChannelScan {
  id: string;
  name: string;
  isPrivate: boolean;
  /** Messages (top-level + thread replies), bytes always 0. */
  text: Bucketed;
  /** image/* files. */
  pics: Bucketed;
  /** video/* files. */
  video: Bucketed;
  /** everything else (docs, audio, archives, …). */
  files: Bucketed;
  /** Per-media-type tallies of files over each Discord upload cap (all-time). */
  overLimit?: { pics: OverLimit; video: OverLimit; files: OverLimit };
}

export interface WorkspaceScan {
  scannedAt: number;
  /** True if any pagination cap was hit — totals are a lower bound. */
  truncated: boolean;
  channels: ChannelScan[];
  /** Connected Discord server's boost level (0–3), or null if unknown. */
  discordPremiumTier?: number | null;
  /** Count of member emails captured this scan (opt-in), or null if not captured. */
  emailsCaptured?: number | null;
}

// Window sizes in milliseconds. 1mo ≈ 30.44 days so 6mo/1y land on calendar-ish
// boundaries without pulling in a date library.
const DAY = 86_400_000;
export const WINDOW_MS: Record<Exclude<TimeBucket, "allTime">, number> = {
  last1y: 365 * DAY,
  last6mo: Math.round(182.5 * DAY),
  last90d: 90 * DAY,
};

export function emptyMetric(): Metric {
  return { count: 0, bytes: 0 };
}

export function emptyBucketed(): Bucketed {
  return {
    allTime: emptyMetric(),
    last1y: emptyMetric(),
    last6mo: emptyMetric(),
    last90d: emptyMetric(),
  };
}

export function emptyOverLimit(): OverLimit {
  return { over10: emptyMetric(), over50: emptyMetric(), over100: emptyMetric() };
}

export function emptyChannelScan(
  id: string,
  name: string,
  isPrivate: boolean,
): ChannelScan {
  return {
    id,
    name,
    isPrivate,
    text: emptyBucketed(),
    pics: emptyBucketed(),
    video: emptyBucketed(),
    files: emptyBucketed(),
    overLimit: {
      pics: emptyOverLimit(),
      video: emptyOverLimit(),
      files: emptyOverLimit(),
    },
  };
}

/** Add a media file's size to the cumulative over-cap tallies. */
export function addOverLimit(o: OverLimit, size: number): void {
  if (size > UPLOAD_THRESHOLDS.t10) {
    o.over10.count += 1;
    o.over10.bytes += size;
  }
  if (size > UPLOAD_THRESHOLDS.t50) {
    o.over50.count += 1;
    o.over50.bytes += size;
  }
  if (size > UPLOAD_THRESHOLDS.t100) {
    o.over100.count += 1;
    o.over100.bytes += size;
  }
}

/**
 * Which time buckets a timestamp (ms) falls into, given `now` (ms). Always
 * includes "allTime". Nested windows are additive from newest outward.
 */
export function bucketsFor(tsMs: number, now: number): TimeBucket[] {
  const out: TimeBucket[] = ["allTime"];
  const age = now - tsMs;
  if (age <= WINDOW_MS.last1y) out.push("last1y");
  if (age <= WINDOW_MS.last6mo) out.push("last6mo");
  if (age <= WINDOW_MS.last90d) out.push("last90d");
  return out;
}

/** Add a (count, bytes) sample at time `tsMs` into every window it belongs to. */
export function addSample(
  b: Bucketed,
  tsMs: number,
  now: number,
  count: number,
  bytes: number,
): void {
  for (const bucket of bucketsFor(tsMs, now)) {
    b[bucket].count += count;
    b[bucket].bytes += bytes;
  }
}

/** Map a Slack file mimetype to our billable content type. */
export function classifyMime(mimetype: string | undefined): "pics" | "video" | "files" {
  const m = (mimetype ?? "").toLowerCase();
  if (m.startsWith("image/")) return "pics";
  if (m.startsWith("video/")) return "video";
  return "files";
}

/** Slack timestamps ("1690000000.000200") → epoch ms. Returns NaN if unparseable. */
export function slackTsToMs(ts: unknown): number {
  if (typeof ts === "number") return ts * 1000;
  if (typeof ts !== "string") return NaN;
  const secs = parseFloat(ts);
  return Number.isFinite(secs) ? secs * 1000 : NaN;
}

// ── Convex validators (shared by the schema table + the storeScan mutation) ──

export const metricValidator = v.object({
  count: v.number(),
  bytes: v.number(),
});
export const bucketedValidator = v.object({
  allTime: metricValidator,
  last1y: metricValidator,
  last6mo: metricValidator,
  last90d: metricValidator,
});
const overLimitValidator = v.object({
  over10: metricValidator,
  over50: metricValidator,
  over100: metricValidator,
});
export const channelScanValidator = v.object({
  id: v.string(),
  name: v.string(),
  isPrivate: v.boolean(),
  text: bucketedValidator,
  pics: bucketedValidator,
  video: bucketedValidator,
  files: bucketedValidator,
  overLimit: v.optional(
    v.object({
      pics: overLimitValidator,
      video: overLimitValidator,
      files: overLimitValidator,
    }),
  ),
});
export const workspaceScanValidator = v.object({
  scannedAt: v.number(),
  truncated: v.boolean(),
  channels: v.array(channelScanValidator),
  discordPremiumTier: v.optional(v.union(v.number(), v.null())),
  emailsCaptured: v.optional(v.union(v.number(), v.null())),
});
